import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { LucideIcon } from "lucide-react";
import { ChevronLeft, ChevronRight, Settings, Search } from "lucide-react";
import {
  Button,
  Input,
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@b2b/ui-components";
import { cn } from "../../lib/utils";

// Hide scrollbar styles
const scrollbarHideStyles = `
  .sidebar-scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .sidebar-scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`;

export interface SideNavItem {
  name: string;
  icon: LucideIcon;
  href: string;
}

export interface SideNavSection {
  title: string;
  items: SideNavItem[];
}

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  className?: string;
  role: "admin" | "buyer" | "seller" | "both";
  navigationItems:
    | SideNavSection[]
    | { buyer: SideNavSection[]; seller: SideNavSection[] };
  /** Seller only: 0-100 profile completion percentage */
  profileCompletionPercentage?: number;
  /** Seller only: when not approved, show step 1/2/3 progress so seller is aware */
  sellerOnboardingProgress?: {
    step1Complete: boolean;
    step2Complete: boolean;
    step3Complete: boolean;
    waitingApproval: boolean;
  };
}

export function Sidebar({
  isCollapsed,
  onToggle,
  className,
  role,
  navigationItems,
  profileCompletionPercentage,
  sellerOnboardingProgress,
}: SidebarProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeRole, setActiveRole] = useState<"buyer" | "seller">("buyer");
  const navigate = useNavigate();

  // pick the correct sidebar items
  let currentNav: SideNavSection[];
  if (role === "both") {
    const bothConfig = navigationItems as {
      buyer: SideNavSection[];
      seller: SideNavSection[];
    };
    currentNav = bothConfig[activeRole];
  } else {
    currentNav = navigationItems as SideNavSection[];
  }

  // Filter navigation based on search query
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const hasSearchQuery = normalizedSearchQuery.length > 0;
  const filteredItems = hasSearchQuery
    ? currentNav
        .map((section) => {
          const sectionMatches = section.title
            .toLowerCase()
            .includes(normalizedSearchQuery);

          if (sectionMatches) {
            // Keep full section items when section title itself matches.
            return section;
          }

          return {
            ...section,
            items: section.items.filter((item) =>
              item.name.toLowerCase().includes(normalizedSearchQuery),
            ),
          };
        })
        .filter((section) => section.items.length > 0)
    : currentNav;

  return (
    <>
      <style>{scrollbarHideStyles}</style>
      <div
        data-app-shell-sidebar="true"
        className={cn(
          "bg-gray-900 border-r border-gray-800 flex flex-col text-gray-100 overflow-hidden",
          isCollapsed ? "w-16" : "w-64",
          className,
        )}
        style={{
          width: isCollapsed ? "4rem" : "16rem",
          transition: "width 300ms ease-in-out",
        }}
      >
        {/* Header (Collapse + Role Toggle if both) */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-800">
          {!isCollapsed && role === "both" && (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={activeRole === "buyer" ? "default" : "ghost"}
                className="text-xs px-2"
                onClick={() => setActiveRole("buyer")}
              >
                Buyer
              </Button>
              <Button
                size="sm"
                variant={activeRole === "seller" ? "default" : "ghost"}
                className="text-xs px-2"
                onClick={() => setActiveRole("seller")}
              >
                Seller
              </Button>
            </div>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
            className="lg:flex text-gray-300 hover:bg-gray-800 hover:text-white transition-colors duration-200"
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Search */}
        {!isCollapsed && (
          <div className="p-4 border-b border-gray-800">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search navigation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-gray-800 border-gray-700 text-gray-100 placeholder:text-gray-400 focus:border-gray-500"
              />
            </div>
          </div>
        )}

        {/* Profile Completion — seller only, expanded sidebar */}
        {!isCollapsed && role === "seller" && profileCompletionPercentage !== undefined && (
          <div className="px-4 py-3 border-b border-gray-800">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium text-gray-300">Profile Completion</span>
              <span
                className={cn(
                  "text-xs font-semibold",
                  profileCompletionPercentage >= 80 ? "text-emerald-400" : "text-orange-400",
                )}
              >
                {profileCompletionPercentage}%
              </span>
            </div>
            <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-500",
                  profileCompletionPercentage >= 80 ? "bg-emerald-500" : "bg-orange-500",
                )}
                style={{ width: `${profileCompletionPercentage}%` }}
              />
            </div>
            {profileCompletionPercentage < 80 && (
              <p className="text-[10px] text-gray-400 mt-1.5 leading-tight">
                Fill all required fields to reach 80% and unlock more features.
              </p>
            )}
          </div>
        )}

        {/* Seller onboarding step progress — so seller is always aware */}
        {!isCollapsed && role === "seller" && sellerOnboardingProgress && (
          <div className="px-4 py-3 border-b border-gray-800">
            <span className="text-xs font-medium text-gray-300 block mb-2">Onboarding progress</span>
            <div className="space-y-1.5 text-[11px]">
              <div className="flex items-center gap-2">
                <span className={sellerOnboardingProgress.step1Complete ? "text-emerald-400" : "text-gray-500"}>
                  {sellerOnboardingProgress.step1Complete ? "✓" : "○"} Step 1: Profile 80%
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className={sellerOnboardingProgress.step2Complete ? "text-emerald-400" : "text-gray-500"}>
                  {sellerOnboardingProgress.step2Complete ? "✓" : "○"} Step 2: Membership plan
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className={sellerOnboardingProgress.step3Complete ? "text-emerald-400" : "text-gray-500"}>
                  {sellerOnboardingProgress.step3Complete ? "✓" : "○"} Step 3: Storefront
                </span>
              </div>
            </div>
            {sellerOnboardingProgress.waitingApproval && (
              <p className="text-[11px] text-amber-400 mt-2 leading-tight">
                All steps complete. Waiting for admin approval.
              </p>
            )}
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto min-h-0 sidebar-scrollbar-hide">
          {!isCollapsed && (
            <Accordion
              key={`sidebar-search-${normalizedSearchQuery || "default"}`}
              type="multiple"
              defaultValue={
                hasSearchQuery
                  ? filteredItems.map((section) => section.title)
                  : []
              }
              className="space-y-2"
            >
              {filteredItems.map((section) => (
                <AccordionItem
                  key={section.title}
                  value={section.title}
                  className="border-none"
                >
                  <AccordionTrigger className="w-full justify-between h-9 px-2 text-sm font-semibold text-gray-200 hover:text-white mb-1 hover:no-underline py-0 transition-colors duration-200">
                    {section.title}
                  </AccordionTrigger>
                  <AccordionContent className="space-y-1 pb-2 transition-all duration-300 ease-in-out">
                    {section.items.map((item) => {
                      const isChat =
                        item.name === "Chat" && item.href === "/chat";
                      const handleClick = () => {
                        if (isChat) {
                          // Open chat in new tab
                          const fullUrl = `${window.location.origin}${item.href}`;
                          window.open(fullUrl, "_blank", "noopener,noreferrer");
                        } else {
                          navigate(item.href);
                        }
                      };
                      return (
                        <Button
                          key={item.name}
                          variant="ghost"
                          size="sm"
                          className="w-full justify-start h-8 px-3 text-xs font-normal hover:bg-gray-800 text-gray-300 hover:text-white transition-colors duration-200 group"
                          onClick={handleClick}
                        >
                          <item.icon className="mr-2.5 h-3.5 w-3.5" />
                          {item.name}
                        </Button>
                      );
                    })}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}

          {/* Collapsed Mode */}
          {isCollapsed && (
            <div className="space-y-2">
              {currentNav.map((section) => (
                <div key={section.title} className="space-y-1">
                  {section.items.map((item) => {
                    const isChat =
                      item.name === "Chat" && item.href === "/chat";
                    const handleClick = () => {
                      if (isChat) {
                        // Open chat in new tab
                        const fullUrl = `${window.location.origin}${item.href}`;
                        window.open(fullUrl, "_blank", "noopener,noreferrer");
                      } else {
                        navigate(item.href);
                      }
                    };
                    return (
                      <Button
                        key={item.name}
                        variant="ghost"
                        size="sm"
                        className="w-full h-9 px-2 justify-center hover:bg-gray-800 text-gray-100 relative group hover:text-white transition-colors duration-200"
                        title={item.name}
                        onClick={handleClick}
                      >
                        <item.icon className="h-4 w-4" />
                      </Button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </nav>

        {/* Footer - Settings only for admin */}
        {/* {role === "admin" && (
          <div className="p-4 border-t border-gray-800">
            <Button
              variant="ghost"
              className={cn(
                "w-full hover:bg-gray-800 text-gray-100 hover:text-white transition-colors duration-200",
                isCollapsed ? "justify-center" : "justify-start",
              )}
              title={isCollapsed ? "Settings" : undefined}
              onClick={() => navigate("/admin/admin_settings")}
            >
              <Settings className={cn("h-4 w-4", !isCollapsed && "mr-3")} />
              {!isCollapsed && "Settings"}
            </Button>
          </div>
        )} */}
      </div>
    </>
  );
}
